import { useState, useEffect, useCallback } from 'react';

import PcbCanvas from './components/PcbCanvas';
import SchCanvas from './components/SchCanvas';
import EdaStyle from './index.style';
import { svgStrFilter, getSquareRowCol, SVGMove, attr, detectBrowser } from './utils';

export default function LcEdaCanvas({
  containerId = 'containerId',
  type = 'sch',
  size = 630,
  data,
  control = true,
  ...props
}) {
  const [schSvgStr, setSchSvgStr] = useState('');
  const [pcbSvgStr, setPcbSvgStr] = useState('');
  const [isGenerated, setIsGenerated] = useState(false);

  let BBox_sch = { x: 0, y: 0, width: size, height: size };
  let BBox_pcb = { x: 0, y: 0, width: size, height: size };
  let N = 1;
  let N2 = 1;
  let gZoom = 1;
  let gZoom2 = 1;
  let gridSize = 10; //网格尺寸
  let historyClientXY_sch;
  let historyClientXY_pcb;

  useEffect(() => {
    if (data?.length && !isGenerated) {
      generateSvgStr(data);
      setIsGenerated(true);
    }
  }, [data, isGenerated]);

  const generateSvgStr = useCallback(
    (data) => {
      let sch_len = 0;
      let maxWidth = 0;
      let maxHeight = 0;
      const bboxArr = [];
      let sch_svgStr = '';
      let pcb_svgStr = '';
      let pcb_index = 1;

      // 确定文档类型
      const indexType = data.some((item) => item.docType === 6) ? 6 : 2;

      data?.forEach((item, i) => {
        let svgStr = svgStrFilter(item.svg);
        svgStr = `<g>${svgStr}</g>`;

        if (item.docType === indexType) {
          sch_len++;
          svgStr = svgStr.replace(/fill="#FFFFFF"|fill="#ffffff"|fill="white"/gi, 'fill="none"');
          sch_svgStr += svgStr;
          const bbox = item.bbox;
          bboxArr.push(bbox);
          maxWidth = Math.max(bbox.width, maxWidth);
          maxHeight = Math.max(bbox.height, maxHeight);
        } else if (item.docType === 4) {
          svgStr = svgStr.replace(/fill="#000000"|fill="black"/gi, 'fill="none"');
          pcb_svgStr += svgStr;
          pcb_index = i;
        }
      });

      if (type === 'sch') {
        setSchSvgStr(sch_svgStr);
        setTimeout(() => {
          normalizeSchSvgStr(sch_len, maxWidth, maxHeight, bboxArr);
          control && bindSchEvent();
        }, 50);
      } else {
        setPcbSvgStr(pcb_svgStr);
        setTimeout(() => {
          normalizePcbSvgStr(data, pcb_index);
          control && bindPcbEvent();
        }, 50);
      }
    },
    [type, control]
  );

  function normalizeSchSvgStr(sch_len, maxWidth, maxHeight, bboxArr) {
    const spacing = 10; //间距默认为10
    const RC = getSquareRowCol(sch_len, maxWidth, maxHeight, spacing);
    BBox_sch = {
      x: 0,
      y: 0,
      width: RC.width,
      height: RC.height,
    };
    const Length = BBox_sch.width > BBox_sch.height ? BBox_sch.width : BBox_sch.height;
    N = size / Length;

    resetRootCanvas();

    // 排列
    const sch_g = document.querySelectorAll(`#${containerId} #g1 > g`);
    let dx = 0;
    let dy = 0;
    let row_now = 1;
    let col_now = 1;
    for (let i = 0; i < sch_g.length; i++) {
      if (col_now > RC.col) {
        row_now++;
        col_now = 1;
      }
      dx = BBox_sch.x - bboxArr[i].x + (maxWidth - bboxArr[i].width) / 2 + (col_now - 1) * (maxWidth + spacing);
      dy = BBox_sch.y - bboxArr[i].y + (row_now - 1) * (maxHeight + spacing);
      SVGMove.g(sch_g[i], dx, dy);
      col_now++;
    }

    // 显示引脚标号和名称
    const schPins = document.querySelectorAll(`#${containerId} #g1 g[c_partid="part_pin"] > text`);
    for (let i = 0; i < schPins.length; i++) {
      attr(schPins[i], 'display', '');
      attr(schPins[i], 'font-size', '4pt');
    }
  }

  function normalizePcbSvgStr(data, pcb_index) {
    if (data[pcb_index].bbox.width) BBox_pcb = data[pcb_index].bbox;
    const Length2 = BBox_pcb.width > BBox_pcb.height ? BBox_pcb.width : BBox_pcb.height;
    N2 = size / Length2;

    resetRootCanvas2();

    // 为焊盘添加编号
    const pads = document.querySelectorAll(`#${containerId} #g12 g[c_partid="part_pad"]`);
    const textTemp = document.querySelector(`#${containerId} #textTemp`);
    const gNets = document.querySelector(`#${containerId} #gNets`);
    let originXY, textEle, num;
    for (let i = 0; i < pads.length; i++) {
      originXY = pads[i].getAttribute('c_origin');
      num = pads[i].getAttribute('number');
      if (originXY) {
        originXY = originXY.split(',');
      }
      if (originXY.length > 1) {
        textEle = textTemp.cloneNode(true);
        textEle.setAttribute('x', originXY[0]);
        textEle.setAttribute('y', originXY[1]);
        textEle.innerHTML = num;
        gNets.appendChild(textEle);
      }
    }
  }

  function resetRootCanvas() {
    let zn = 100 * N * 0.8;
    zn = zn > 3000 ? 3000 : zn;
    zn = zn < 25 ? 25 : zn;
    zoom(zn);
  }

  function resetRootCanvas2() {
    let zn = 100 * N2 * 0.8;
    zn = zn > 3000 ? 3000 : zn;
    zn = zn < 25 ? 25 : zn;
    zoom2(zn);
  }

  function zoom(n, clientXY) {
    const canvas = document.querySelector(`#${containerId} #root`);

    if (!canvas || !canvas.viewBox) {
      return;
    }

    if (n > 3000 || n < 25) {
      return;
    }
    const gZoomOld = gZoom;
    gZoom = n / 100;

    let vbNew = [];
    const vbOld = canvas.viewBox.baseVal;
    const W = +canvas.getAttribute('width');
    const H = +canvas.getAttribute('height');

    //鼠标在画布中（非边沿）的时候，使用鼠标点为中心缩放，否则使用bbox中心为中心缩放(手机上以屏幕中心缩放)
    if (clientXY) {
      vbNew = [
        vbOld.x + clientXY.x / gZoomOld - clientXY.x / gZoom,
        vbOld.y + clientXY.y / gZoomOld - clientXY.y / gZoom,
        W / gZoom,
        H / gZoom,
      ];
      historyClientXY_sch = clientXY; //记录
    } else {
      const bbox = BBox_sch;
      const svgXY = {
        x: bbox.width ? bbox.x + bbox.width / 2 : bbox.x || vbOld.x + vbOld.width / 2,
        y: bbox.height ? bbox.y + bbox.height / 2 : bbox.y || vbOld.y + vbOld.height / 2,
      };
      //且要将缩放后的画面置于视野中心
      vbNew = [svgXY.x - W / gZoom / 2, svgXY.y - H / gZoom / 2, W / gZoom, H / gZoom];
    }

    const viewbox = {
      x: vbNew[0],
      y: vbNew[1],
      width: vbNew[2],
      height: vbNew[3],
    };
    const viewBoxStr = viewbox.x + ' ' + viewbox.y + ' ' + viewbox.width + ' ' + viewbox.height;
    canvas.setAttribute('viewBox', ' ' + viewBoxStr);
    updateCanvas(viewbox);

    //细节调整
    const gridCell2 = document.querySelector(`#${containerId} #gridCell2`);
    const gridOH = document.querySelector(`#${containerId} #gridOH`);
    const gridOV = document.querySelector(`#${containerId} #gridOV`);
    const px1 = 100 / n;
    gridCell2.setAttribute('stroke-width', px1);
    gridOH.setAttribute('stroke-width', px1);
    gridOV.setAttribute('stroke-width', px1);

    if (gridSize < 1) {
      gridSize = 1;
    }
    while (gridSize * gZoom < 5) {
      if (gridSize * gZoom * 2 >= 5) {
        gridSize *= 2;
      } else if (gridSize * gZoom * 5 >= 5) {
        gridSize *= 5;
      } else {
        gridSize *= 10;
      }
    }
    if (gZoom >= 1) {
      gridSize = 10;
    }
    const gridPattern2 = document.querySelector(`#${containerId} #${containerId}_gridPattern2`);
    gridPattern2.setAttribute('width', gridSize);
    gridPattern2.setAttribute('height', gridSize);
    gridCell2.setAttribute('d', 'M10 0V10H0'.replace(/10/g, gridSize));
  }

  function zoom2(n, clientXY) {
    const canvas2 = document.querySelector(`#${containerId} #root2`);

    if (!canvas2 || !canvas2.viewBox) {
      return;
    }

    if (n > 3000 || n < 25) {
      return;
    }
    const gZoomOld = gZoom2;
    gZoom2 = n / 100;

    let vbNew = [];
    const vbOld = canvas2.viewBox.baseVal;
    const W = +canvas2.getAttribute('width');
    const H = +canvas2.getAttribute('height');

    //鼠标在画布中（非边沿）的时候，使用鼠标点为中心缩放，否则使用bbox中心为中心缩放(手机上以屏幕中心缩放)
    if (clientXY) {
      vbNew = [
        vbOld.x + clientXY.x / gZoomOld - clientXY.x / gZoom2,
        vbOld.y + clientXY.y / gZoomOld - clientXY.y / gZoom2,
        W / gZoom2,
        H / gZoom2,
      ];
      historyClientXY_pcb = clientXY; //记录
    } else {
      const bbox = BBox_pcb;
      const svgXY = {
        x: bbox.width ? bbox.x + bbox.width / 2 : bbox.x || vbOld.x + vbOld.width / 2,
        y: bbox.height ? bbox.y + bbox.height / 2 : bbox.y || vbOld.y + vbOld.height / 2,
      };
      //且要将缩放后的画面置于视野中心
      vbNew = [svgXY.x - W / gZoom2 / 2, svgXY.y - H / gZoom2 / 2, W / gZoom2, H / gZoom2];
    }

    const viewbox = {
      x: vbNew[0],
      y: vbNew[1],
      width: vbNew[2],
      height: vbNew[3],
    };
    const viewBoxStr = viewbox.x + ' ' + viewbox.y + ' ' + viewbox.width + ' ' + viewbox.height;
    canvas2.setAttribute('viewBox', ' ' + viewBoxStr);
    updateCanvas2(viewbox);

    //细节调整
    const gridCell2 = document.querySelector(`#${containerId} #gridCell22`);
    const gridOH = document.querySelector(`#${containerId} #gridOH2`);
    const gridOV = document.querySelector(`#${containerId} #gridOV2`);
    const px1 = 100 / n;
    gridCell2.setAttribute('stroke-width', px1);
    gridOH.setAttribute('stroke-width', px1);
    gridOV.setAttribute('stroke-width', px1);

    if (gridSize < 1) {
      gridSize = 1;
    }
    while (gridSize * gZoom2 < 5) {
      if (gridSize * gZoom2 * 2 >= 5) {
        gridSize *= 2;
      } else if (gridSize * gZoom2 * 5 >= 5) {
        gridSize *= 5;
      } else {
        gridSize *= 10;
      }
    }
    if (gZoom2 >= 1) {
      gridSize = 10;
    }
    const gridPattern2 = document.querySelector(`#${containerId} #${containerId}_gridPattern22`);
    gridPattern2.setAttribute('width', gridSize);
    gridPattern2.setAttribute('height', gridSize);
    gridCell2.setAttribute('d', 'M10 0V10H0'.replace(/10/g, gridSize));
  }

  function updateCanvas(viewBox) {
    const grid1 = document.querySelector(`#${containerId} #grid1`);
    const gridOH = document.querySelector(`#${containerId} #gridOH`);
    const gridOV = document.querySelector(`#${containerId} #gridOV`);

    grid1.setAttribute('x', viewBox.x);
    grid1.setAttribute('y', viewBox.y);
    grid1.setAttribute('width', viewBox.width);
    grid1.setAttribute('height', viewBox.height);
    gridOH.setAttribute('x1', viewBox.x);
    gridOH.setAttribute('y1', '180');
    gridOH.setAttribute('x2', viewBox.width + viewBox.x);
    gridOH.setAttribute('y2', '180');
    gridOV.setAttribute('x1', '180');
    gridOV.setAttribute('y1', viewBox.y);
    gridOV.setAttribute('x2', '180');
    gridOV.setAttribute('y2', viewBox.height + viewBox.y);
  }

  function updateCanvas2(viewBox) {
    const grid1 = document.querySelector(`#${containerId} #grid12`);
    const gridOH = document.querySelector(`#${containerId} #gridOH2`);
    const gridOV = document.querySelector(`#${containerId} #gridOV2`);
    const gridBg = document.querySelector(`#${containerId} #gridBg`);

    grid1.setAttribute('x', viewBox.x);
    grid1.setAttribute('y', viewBox.y);
    grid1.setAttribute('width', viewBox.width);
    grid1.setAttribute('height', viewBox.height);
    gridOH.setAttribute('x1', viewBox.x);
    gridOH.setAttribute('y1', '180');
    gridOH.setAttribute('x2', viewBox.width + viewBox.x);
    gridOH.setAttribute('y2', '180');
    gridOV.setAttribute('x1', '180');
    gridOV.setAttribute('y1', viewBox.y);
    gridOV.setAttribute('x2', '180');
    gridOV.setAttribute('y2', viewBox.height + viewBox.y);
    gridBg.setAttribute('x', viewBox.x);
    gridBg.setAttribute('y', viewBox.y);
    gridBg.setAttribute('width', viewBox.width);
    gridBg.setAttribute('height', viewBox.height);
  }

  function bindSchEvent() {
    const { isFirefox, isIE, isEdge } = detectBrowser();
    const canvas = document.querySelector(`#${containerId} #root`);

    if (!canvas || !canvas.viewBox) {
      return;
    }

    let LEFT, TOP;

    // 移动事件
    canvas.addEventListener('mousedown', (e_down) => {
      let isdown = true;
      let downX = e_down.offsetX;
      let downY = e_down.offsetY;
      if ((isFirefox || isIE || isEdge) && e_down.target.tagName !== 'svg') {
        const rect = canvas.getBoundingClientRect();
        LEFT = rect.left;
        TOP = rect.top;
        downX = e_down.pageX - LEFT;
        downY = e_down.pageY - TOP;
      }

      function handleMouseMove(e_move) {
        if (isdown) {
          let moveX = e_move.offsetX;
          let moveY = e_move.offsetY;
          if ((isFirefox || isIE || isEdge) && e_move.target.tagName !== 'svg') {
            moveX = e_move.pageX - LEFT;
            moveY = e_move.pageY - TOP;
          }
          const dx = downX - moveX;
          const dy = downY - moveY;
          if (dx !== 0 && dy !== 0) {
            //更新viewBox
            const viewbox = canvas.viewBox.baseVal;
            viewbox.x += dx / gZoom;
            viewbox.y += dy / gZoom;
            const viewBoxStr = viewbox.x + ' ' + viewbox.y + ' ' + viewbox.width + ' ' + viewbox.height;
            canvas.setAttribute('viewBox', ' ' + viewBoxStr);
            //更新画布
            updateCanvas(viewbox);
            //刷新坐标
            downX = moveX;
            downY = moveY;
          }
        }
      }

      function handleMouseUp() {
        isdown = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });

    // 阻止右键菜单
    canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // 缩放事件
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -1 : 1;
      if (delta !== 0) {
        let clientXY = {
          x: e.offsetX,
          y: e.offsetY,
        };
        if ((isFirefox || isIE || isEdge) && e.target.tagName !== 'svg') {
          clientXY = {
            x: e.pageX - LEFT,
            y: e.pageY - TOP,
          };
        }
        if (delta > 0) {
          zoom(100 * gZoom * 1.2, clientXY);
        } else {
          zoom((100 * gZoom) / 1.2, clientXY);
        }
      }
    });

    // 控制按钮
    document.querySelector(`#${containerId} #rootlarger`)?.addEventListener('click', () => {
      zoom(1.2 * gZoom * 100, historyClientXY_sch);
    });
    document.querySelector(`#${containerId} #rootsmaller`)?.addEventListener('click', () => {
      zoom((gZoom / 1.2) * 100, historyClientXY_sch);
    });
    document.querySelector(`#${containerId} #rootreset`)?.addEventListener('click', resetRootCanvas);
  }

  function bindPcbEvent() {
    const { isFirefox, isIE, isEdge } = detectBrowser();
    const canvas2 = document.querySelector(`#${containerId} #root2`);

    if (!canvas2 || !canvas2.viewBox) {
      return;
    }

    let LEFT2, TOP2;

    // 移动事件
    canvas2.addEventListener('mousedown', (e_down) => {
      let isdown = true;
      let downX = e_down.offsetX;
      let downY = e_down.offsetY;
      if ((isFirefox || isIE || isEdge) && e_down.target.tagName !== 'svg') {
        const rect = canvas2.getBoundingClientRect();
        LEFT2 = rect.left;
        TOP2 = rect.top;
        downX = e_down.pageX - LEFT2;
        downY = e_down.pageY - TOP2;
      }

      function handleMouseMove(e_move) {
        if (isdown) {
          let moveX = e_move.offsetX;
          let moveY = e_move.offsetY;
          if ((isFirefox || isIE || isEdge) && e_move.target.tagName !== 'svg') {
            moveX = e_move.pageX - LEFT2;
            moveY = e_move.pageY - TOP2;
          }
          const dx = downX - moveX;
          const dy = downY - moveY;
          if (dx !== 0 && dy !== 0) {
            //更新viewBox
            const viewbox = canvas2.viewBox.baseVal;
            viewbox.x += dx / gZoom2;
            viewbox.y += dy / gZoom2;
            const viewBoxStr = viewbox.x + ' ' + viewbox.y + ' ' + viewbox.width + ' ' + viewbox.height;
            canvas2.setAttribute('viewBox', ' ' + viewBoxStr);
            //更新画布
            updateCanvas2(viewbox);
            //刷新坐标
            downX = moveX;
            downY = moveY;
          }
        }
      }

      function handleMouseUp() {
        isdown = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      }

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    });

    // 阻止右键菜单
    canvas2.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // 缩放事件
    canvas2.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -1 : 1;
      if (delta !== 0) {
        let clientXY = {
          x: e.offsetX,
          y: e.offsetY,
        };
        if ((isFirefox || isIE || isEdge) && e.target.tagName !== 'svg') {
          clientXY = {
            x: e.pageX - LEFT2,
            y: e.pageY - TOP2,
          };
        }
        if (delta > 0) {
          zoom2(100 * gZoom2 * 1.2, clientXY);
        } else {
          zoom2((100 * gZoom2) / 1.2, clientXY);
        }
      }
    });

    // 控制按钮
    document.querySelector(`#${containerId} #rootlarger2`)?.addEventListener('click', () => {
      zoom2(1.2 * gZoom2 * 100, historyClientXY_pcb);
    });
    document.querySelector(`#${containerId} #rootsmaller2`)?.addEventListener('click', () => {
      zoom2((gZoom2 / 1.2) * 100, historyClientXY_pcb);
    });
    document.querySelector(`#${containerId} #rootreset2`)?.addEventListener('click', resetRootCanvas2);
  }

  return (
    <EdaStyle>
      <div id="lcEdaCanvas">
        {type === 'sch' ? (
          <SchCanvas containerId={containerId} svgStr={schSvgStr} size={size} control={control} {...props} />
        ) : (
          <PcbCanvas containerId={containerId} svgStr={pcbSvgStr} size={size} control={control} {...props} />
        )}
      </div>
    </EdaStyle>
  );
}
